import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import {
  ArrowLeft,
  Mail,
  MapPin,
  Building2,
  Calendar,
  Phone,
  Activity,
  Image as ImageIcon,
  Crop,
  Camera,
  Upload,
  Edit2,
  X,
  Save,
  Crown,
  CheckCircle,
  CreditCard,
  Plus,
  Trash2,
  Layers
} from "lucide-react";
import Swal from "sweetalert2";
import { API_BASE_URL } from "../config";

export default function UserView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [rawUser, setRawUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingFrames, setUploadingFrames] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [selectedDirectFrames, setSelectedDirectFrames] = useState([]);

  // User Custom Structured Frames (Top + Bottom + Aspect Ratios + Thumbnail)
  const [userFrames, setUserFrames] = useState([]);
  const [loadingFrames, setLoadingFrames] = useState(false);
  const [previewFrame, setPreviewFrame] = useState(null);
  const [isAddFramesModalOpen, setIsAddFramesModalOpen] = useState(false);
  const [frameRows, setFrameRows] = useState([
    {
      top_image: null,
      top_image_preview: null,
      bottom_image: null,
      bottom_image_preview: null,
      thumbnail_image: null,
      thumbnail_image_preview: null,
      top_aspect_ratio: "1080:400",
      bottom_aspect_ratio: "1080:400"
    }
  ]);
  const [uploadingStructuredFrames, setUploadingStructuredFrames] = useState(false);

  // Edit Modal State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [modalFormData, setModalFormData] = useState({
    fullname: "",
    email: "",
    city: "",
    business_name: "",
    business_email: "",
    business_phone: "",
    business_address: "",
    designation: "",
    status: "Free"
  });
  const [modalUserImage, setModalUserImage] = useState(null);
  const [modalUserImagePreview, setModalUserImagePreview] = useState(null);
  const [modalFrames, setModalFrames] = useState([]);
  const [modalFramesPreview, setModalFramesPreview] = useState([]);
  const [modalBusinessLogo, setModalBusinessLogo] = useState(null);
  const [modalBusinessLogoPreview, setModalBusinessLogoPreview] = useState(null);
  const [savingModal, setSavingModal] = useState(false);

  // Upgrade Plan Modal State
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [customDuration, setCustomDuration] = useState("30");
  const [assigningPlan, setAssigningPlan] = useState(false);

  // Active Subscription State
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);

  const fetchActiveSubscription = () => {
    setSubscriptionLoading(true);
    fetch(`${API_BASE_URL}/api/user-subscriptions/active/${id}`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch active subscription");
        return res.json();
      })
      .then((resJson) => {
        if (resJson && resJson.status) {
          setActiveSubscription(resJson);
        } else {
          setActiveSubscription(null);
        }
        setSubscriptionLoading(false);
      })
      .catch((err) => {
        console.error("Active subscription fetch error:", err);
        setActiveSubscription(null);
        setSubscriptionLoading(false);
      });
  };

  // Hidden File Input Refs
  const userImageInputRef = useRef(null);
  const framesInputRef = useRef(null);

  const fetchUserDetails = () => {
    setLoading(true);
    fetch(`${API_BASE_URL}/api/users/user-details/${id}`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch user details");
        return res.json();
      })
      .then((resJson) => {
        if (resJson && resJson.status && resJson.data) {
          const u = resJson.data;
          setRawUser(u);
          setUser({
            id: u.user_id_PK,
            userimage: u.userimage || "https://i.pravatar.cc/150",
            fullName: u.fullname || "N/A",
            email: u.email || "N/A",
            phone: (u.country_code || "") + " " + (u.mobile_number || ""),
            city: u.city || "N/A",
            designation: u.designation || "N/A",
            joinDate: u.created_at
              ? new Date(u.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
              : "N/A",
            is_verified: (u.is_verified !== undefined && u.is_verified !== null) ? Number(u.is_verified) : 1,
            status: ((u.is_verified !== undefined && u.is_verified !== null) ? Number(u.is_verified) : 1) === 0 ? "Premium" : "Free",
            businessName: u.business_name || "N/A",
            businessLogo: u.business_logo || "https://i.pravatar.cc/150",
            frames: Array.isArray(u.frames) ? u.frames : (typeof u.frames === 'string' && u.frames ? u.frames.split(',') : []),
            businessEmail: u.business_email || "N/A",
            businessPhone: u.business_phone || "N/A",
            businessAddress: u.business_address || "N/A",
            stats: {
              templatesShared: 0,
              bgRemovals: 0,
              activeDays: 0
            },
            subscription: u.subscription ? {
              planName: u.subscription.plan_name || "Premium Plan",
              startDate: u.subscription.start_date ? new Date(u.subscription.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : "N/A",
              expiryDate: u.subscription.end_date ? new Date(u.subscription.end_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : "N/A",
              price: u.subscription.price ? `₹${u.subscription.price}` : "Custom"
            } : null
          });
          if (Array.isArray(u.frames)) {
            setUserFrames(u.frames);
          }
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("User details fetch error:", err);
        setLoading(false);
      });
  };

  const fetchUserFrames = () => {
    setLoadingFrames(true);
    fetch(`${API_BASE_URL}/api/user-frames/user/${id}`)
      .then((res) => res.json())
      .then((resJson) => {
        if (resJson && resJson.status && Array.isArray(resJson.data)) {
          setUserFrames(resJson.data);
        } else {
          setUserFrames([]);
        }
        setLoadingFrames(false);
      })
      .catch((err) => {
        console.error("User frames fetch error:", err);
        setUserFrames([]);
        setLoadingFrames(false);
      });
  };

  useEffect(() => {
    fetchUserDetails();
    fetchActiveSubscription();
    fetchUserFrames();
  }, [id]);

  const handleAddFrameRow = () => {
    setFrameRows((prev) => [
      ...prev,
      {
        top_image: null,
        top_image_preview: null,
        bottom_image: null,
        bottom_image_preview: null,
        thumbnail_image: null,
        thumbnail_image_preview: null,
        top_aspect_ratio: "1080:400",
        bottom_aspect_ratio: "1080:400"
      }
    ]);
  };

  const handleRemoveFrameRow = (index) => {
    if (frameRows.length <= 1) return;
    setFrameRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFrameRowChange = (index, field, value) => {
    setFrameRows((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  const handleFrameRowFileChange = (index, field, file) => {
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    setFrameRows((prev) => {
      const copy = [...prev];
      copy[index] = {
        ...copy[index],
        [field]: file,
        [`${field}_preview`]: previewUrl
      };
      return copy;
    });
  };

  const handleOpenAddFramesModal = () => {
    setFrameRows([
      {
        top_image: null,
        top_image_preview: null,
        bottom_image: null,
        bottom_image_preview: null,
        thumbnail_image: null,
        thumbnail_image_preview: null,
        top_aspect_ratio: "1080:400",
        bottom_aspect_ratio: "1080:400"
      }
    ]);
    setIsAddFramesModalOpen(true);
  };

  const handleSaveStructuredFrames = async () => {
    const validRows = frameRows.filter(
      (r) => r.top_image || r.bottom_image || r.thumbnail_image
    );
    if (validRows.length === 0) {
      Swal.fire("Images Required", "Please select at least one Top Image, Bottom Image, or Thumbnail.", "warning");
      return;
    }

    const formData = new FormData();
    formData.append("userid", id);

    frameRows.forEach((row, idx) => {
      if (row.top_image) formData.append(`top_image_${idx}`, row.top_image);
      if (row.bottom_image) formData.append(`bottom_image_${idx}`, row.bottom_image);
      if (row.thumbnail_image) formData.append(`thumbnail_${idx}`, row.thumbnail_image);
      formData.append(`top_aspect_ratio_${idx}`, row.top_aspect_ratio || "1080:400");
      formData.append(`bottom_aspect_ratio_${idx}`, row.bottom_aspect_ratio || "1080:400");
    });

    try {
      setUploadingStructuredFrames(true);
      const res = await fetch(`${API_BASE_URL}/api/user-frames/add`, {
        method: "POST",
        body: formData
      });
      const data = await res.json();
      setUploadingStructuredFrames(false);

      if (data.status) {
        setIsAddFramesModalOpen(false);
        Swal.fire({
          icon: "success",
          title: "Frames Added!",
          text: data.message || "Frames added successfully in bulk!",
          timer: 2000,
          showConfirmButton: false
        });
        fetchUserFrames();
      } else {
        Swal.fire("Error", data.message || "Failed to add frames", "error");
      }
    } catch (err) {
      setUploadingStructuredFrames(false);
      console.error("Frame upload error:", err);
      Swal.fire("Error", "Something went wrong while uploading frames", "error");
    }
  };

  const handleDeleteStructuredFrame = (frameId) => {
    Swal.fire({
      title: "Delete Frame?",
      text: "Are you sure you want to delete this frame?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await fetch(`${API_BASE_URL}/api/user-frames/${frameId}`, {
            method: "DELETE"
          });
          const data = await res.json();
          if (data.status) {
            Swal.fire({
              icon: "success",
              title: "Deleted!",
              text: "Frame has been deleted.",
              timer: 1500,
              showConfirmButton: false
            });
            fetchUserFrames();
          } else {
            Swal.fire("Error", data.message || "Failed to delete frame.", "error");
          }
        } catch (err) {
          Swal.fire("Error", "Something went wrong.", "error");
        }
      }
    });
  };

  // Direct User Profile Image / PNG upload
  const handleUserImageChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    if (!file.type.match(/^image\/(png|jpeg|jpg|webp)$/i)) {
      Swal.fire("Invalid File", "Please select a PNG, JPG, or WEBP image file.", "error");
      return;
    }

    const formData = new FormData();
    formData.append("user_id", id);
    formData.append("userimage", file);
    if (rawUser) {
      if (rawUser.fullname) formData.append("fullname", rawUser.fullname);
      if (rawUser.email) formData.append("email", rawUser.email);
    }

    try {
      setUploadingImage(true);
      const res = await fetch(`${API_BASE_URL}/api/users/add-user-details`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      setUploadingImage(false);

      if (data.status) {
        Swal.fire({
          icon: "success",
          title: "Image Uploaded",
          text: "User profile image uploaded successfully!",
          timer: 2000,
          showConfirmButton: false,
        });
        fetchUserDetails();
      } else {
        Swal.fire("Error", data.message || "Failed to upload image", "error");
      }
    } catch (err) {
      setUploadingImage(false);
      console.error("Upload error:", err);
      Swal.fire("Error", "Something went wrong during upload", "error");
    } finally {
      if (userImageInputRef.current) userImageInputRef.current.value = "";
    }
  };

  // Direct Frames bulk upload - Stage 1 (Selection)
  const handleFramesChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setSelectedDirectFrames((prev) => [...prev, ...Array.from(files)]);
    }
  };

  // Direct Frames bulk upload - Stage 2 (Upload API)
  const handleUploadSelectedFrames = async () => {
    if (!selectedDirectFrames || selectedDirectFrames.length === 0) return;

    const formData = new FormData();
    formData.append("user_id", id);
    for (let i = 0; i < selectedDirectFrames.length; i++) {
      formData.append("frames", selectedDirectFrames[i]);
    }

    try {
      setUploadingFrames(true);
      const res = await fetch(`${API_BASE_URL}/api/users/add-bulk-frames`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      setUploadingFrames(false);

      if (data.status) {
        Swal.fire({
          icon: "success",
          title: "Frames Uploaded",
          text: "Frames uploaded successfully in bulk!",
          timer: 2000,
          showConfirmButton: false,
        });
        setSelectedDirectFrames([]);
        fetchUserDetails();
      } else {
        Swal.fire("Error", data.message || "Failed to upload frames", "error");
      }
    } catch (err) {
      setUploadingFrames(false);
      console.error("Upload error:", err);
      Swal.fire("Error", "Something went wrong during upload", "error");
    } finally {
      if (framesInputRef.current) framesInputRef.current.value = "";
    }
  };

  const handleRemoveFrame = async (frameUrl) => {
    Swal.fire({
      title: 'Remove Frame?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, remove it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await fetch(`${API_BASE_URL}/api/users/remove-frame`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_id: id, frame_url: frameUrl })
          });
          const data = await res.json();
          if (data.status) {
            Swal.fire('Removed!', 'The frame has been removed.', 'success');
            fetchUserDetails();
          } else {
            Swal.fire('Error', data.message || 'Failed to remove frame.', 'error');
          }
        } catch (error) {
          Swal.fire('Error', 'Something went wrong.', 'error');
        }
      }
    });
  };

  // Open Edit Modal with current data
  const handleOpenEditModal = () => {
    if (!rawUser) return;
    setModalFormData({
      fullname: rawUser.fullname || "",
      email: rawUser.email || "",
      city: rawUser.city || "",
      business_name: rawUser.business_name || "",
      business_email: rawUser.business_email || "",
      business_phone: rawUser.business_phone || "",
      business_address: rawUser.business_address || "",
      designation: rawUser.designation || "",
      status: Number(rawUser.is_verified) === 0 ? "Premium" : "Free"
    });
    setModalUserImage(null);
    setModalUserImagePreview(null);
    setModalBusinessLogo(null);
    setModalBusinessLogoPreview(rawUser.business_logo || null);
    setModalFrames([]);
    setModalFramesPreview([]);
    setIsEditModalOpen(true);
  };

  // Save Modal Form
  const handleSaveModal = async (e) => {
    e.preventDefault();
    if (!modalFormData.fullname.trim() || !modalFormData.email.trim()) {
      Swal.fire("Required Fields", "Full name and email are required.", "warning");
      return;
    }

    const formData = new FormData();
    formData.append("user_id", id);
    formData.append("fullname", modalFormData.fullname.trim());
    formData.append("email", modalFormData.email.trim());
    formData.append("city", modalFormData.city.trim());
    formData.append("business_name", modalFormData.business_name.trim());
    formData.append("business_email", modalFormData.business_email.trim());
    formData.append("business_phone", modalFormData.business_phone.trim());
    formData.append("business_address", modalFormData.business_address.trim());
    formData.append("designation", modalFormData.designation.trim());

    if (modalUserImage) {
      formData.append("userimage", modalUserImage);
    }
    if (modalBusinessLogo) {
      formData.append("business_logo", modalBusinessLogo);
    }
    if (modalFrames && modalFrames.length > 0) {
      for (let i = 0; i < modalFrames.length; i++) {
        formData.append("frames", modalFrames[i]);
      }
    }

    try {
      setSavingModal(true);
      const res = await fetch(`${API_BASE_URL}/api/users/add-user-details`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();

      // If status changed, update verification status
      if (modalFormData.status) {
        await fetch(`${API_BASE_URL}/api/user-subscriptions/set-user-status`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: id,
            status: modalFormData.status,
            is_verified: modalFormData.status === "Premium" ? 0 : 1
          })
        });
      }

      setSavingModal(false);

      if (data.status) {
        setIsEditModalOpen(false);
        Swal.fire({
          icon: "success",
          title: "Updated!",
          text: "User profile updated successfully!",
          timer: 2000,
          showConfirmButton: false,
        });
        fetchUserDetails();
      } else {
        Swal.fire("Error", data.message || "Failed to update user", "error");
      }
    } catch (err) {
      setSavingModal(false);
      console.error("Save modal error:", err);
      Swal.fire("Error", "Something went wrong while saving", "error");
    }
  };

  // Open Upgrade Plan Modal
  const handleOpenUpgradeModal = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/plans/all`);
      const json = await res.json();
      if (json.status && json.data) {
        setAvailablePlans(json.data);
        if (json.data.length > 0) {
          setSelectedPlanId(json.data[0].plan_id_PK);
          setCustomDuration(String(json.data[0].duration_days || 30));
        }
      }
    } catch (e) {
      console.error("Error fetching plans:", e);
    }
    setIsUpgradeModalOpen(true);
  };

  // Submit Plan Assignment (sets is_verified = 0)
  const handleAssignPlan = async () => {
    try {
      setAssigningPlan(true);
      const res = await fetch(`${API_BASE_URL}/api/user-subscriptions/assign-plan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: id,
          plan_id: selectedPlanId || null,
          duration_days: customDuration
        })
      });
      const data = await res.json().catch(() => ({ status: false, message: "Invalid server response" }));
      setAssigningPlan(false);
      if (data.status) {
        setIsUpgradeModalOpen(false);
        Swal.fire({
          icon: "success",
          title: "Plan Assigned!",
          text: "User is now Premium (is_verified: 0)",
          timer: 2000,
          showConfirmButton: false
        });
        fetchUserDetails();
        fetchActiveSubscription();
      } else {
        Swal.fire("Error", data.message || "Failed to assign plan", "error");
      }
    } catch (err) {
      setAssigningPlan(false);
      console.error("Assign plan error:", err);
      Swal.fire("Error", "Something went wrong while assigning plan", "error");
    }
  };

  // Quick toggle between Premium (0) and Free (1)
  const handleToggleVerifiedStatus = async (targetStatus) => {
    const targetVerified = targetStatus === "Premium" ? 0 : 1;
    const result = await Swal.fire({
      title: targetStatus === "Premium" ? "Upgrade to Premium?" : "Switch to Free?",
      text: `User will be marked as ${targetStatus} (is_verified will be set to ${targetVerified}).`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: targetStatus === "Premium" ? "#f59e0b" : "#4f46e5",
      confirmButtonText: `Yes, Set is_verified = ${targetVerified}`
    });

    if (result.isConfirmed) {
      try {
        const res = await fetch(`${API_BASE_URL}/api/user-subscriptions/set-user-status`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: id,
            status: targetStatus,
            is_verified: targetVerified
          })
        });
        const data = await res.json().catch(() => ({ status: false, message: "Invalid server response" }));
        if (data.status) {
          Swal.fire({
            icon: "success",
            title: "Status Updated!",
            text: `User is now ${targetStatus} (is_verified: ${targetVerified})`,
            timer: 2000,
            showConfirmButton: false
          });
          fetchUserDetails();
          fetchActiveSubscription();
        } else {
          Swal.fire("Error", data.message || "Failed to update status", "error");
        }
      } catch (err) {
        console.error("Toggle status error:", err);
        Swal.fire("Error", "Something went wrong", "error");
      }
    }
  };

  const getRemainingDays = (expiryDateStr) => {
    if (!expiryDateStr) return 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const parts = expiryDateStr.split('-');
    if (parts.length < 3) return 0;
    const day = parseInt(parts[0], 10);
    const monthStr = parts[1];
    const year = parseInt(parts[2], 10);
    const months = {
      Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
      Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11
    };
    const expiry = new Date(year, months[monthStr], day);
    expiry.setHours(0, 0, 0, 0);

    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const subData = activeSubscription?.data;
  const hasActivePlan = Boolean(
    activeSubscription?.status &&
    subData &&
    (subData.status === "active" || activeSubscription.is_premium) &&
    subData.plan_name &&
    subData.plan_name !== "Free Plan"
  );

  return (
    <div className="flex bg-[#f8fafc] dark:bg-slate-900 min-h-screen text-slate-800 dark:text-slate-100 font-sans selection:bg-blue-100 dark:selection:bg-blue-900 relative overflow-hidden">
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      {/* Hidden File Inputs for Quick Upload */}
      <input
        type="file"
        ref={userImageInputRef}
        onChange={handleUserImageChange}
        accept="image/png, image/jpeg, image/jpg, image/webp"
        className="hidden"
      />
      <input
        type="file"
        ref={framesInputRef}
        onChange={handleFramesChange}
        accept="image/png, image/jpeg, image/jpg, image/webp"
        multiple
        className="hidden"
      />

      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen relative z-10 w-full lg:w-[calc(100%-16rem)] overflow-y-auto">
        <Header
          onMenuClick={() => setIsSidebarOpen(true)}
          title="{user.fullName}"
          subtitle="Custom frames for this user."
        />

        <div className="p-4 sm:p-8 flex-1 w-full max-w-7xl mx-auto pb-20">

          {/* Header Action */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigate('/users')}
              className="flex items-center gap-2 text-slate-500 dark:text-slate-400 hover:text-blue-600 transition-colors text-sm font-medium w-fit"
            >
              <ArrowLeft size={16} /> Back to Users
            </button>

            {user && (
              <button
                onClick={handleOpenEditModal}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold shadow-sm hover:shadow-md transition-all"
              >
                <Edit2 size={16} /> Edit Profile & Images
              </button>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center p-20">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="ml-3 text-slate-600 dark:text-slate-300 font-medium">Loading user details...</span>
            </div>
          ) : !user ? (
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-8 text-center text-slate-500 dark:text-slate-400 font-medium">
              User details not found.
            </div>
          ) : (
            <div className="flex flex-col xl:flex-row gap-8">

              {/* Left & Center: Admin Data */}
              <div className="flex-1 flex flex-col gap-6">

                {/* User Profile Card */}
                <div className="bg-white dark:bg-slate-800 shadow-sm rounded-2xl border border-slate-200 dark:border-slate-700 p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 relative overflow-hidden">
                  <div className="flex items-center gap-5">
                    {/* User Avatar with Quick PNG / Image Upload */}
                    <div
                      className="relative group cursor-pointer"
                      onClick={() => !uploadingImage && userImageInputRef.current && userImageInputRef.current.click()}
                      title="Click to upload/change User Image (PNG/JPG)"
                    >
                      <div className="w-24 h-24 rounded-2xl overflow-hidden border-2 border-slate-200 dark:border-slate-700 shadow-sm bg-slate-100 dark:bg-slate-700 relative">
                        <img
                          src={user.userimage}
                          alt={user.fullName}
                          className="w-full h-full object-cover"
                        />
                        {/* Hover Overlay */}
                        <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-all flex flex-col items-center justify-center text-white gap-1 p-1 text-center">
                          <Camera size={20} />
                          <span className="text-[10px] font-semibold leading-tight">Upload PNG</span>
                        </div>
                        {/* Uploading Spinner */}
                        {uploadingImage && (
                          <div className="absolute inset-0 bg-slate-900/70 flex items-center justify-center">
                            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        )}
                      </div>

                      <div className={`absolute -bottom-2 -right-2 text-white text-[10px] font-bold px-2 py-0.5 rounded-lg border-2 border-white dark:border-slate-800 shadow-sm ${user.is_verified === 0 ? 'bg-amber-500' : 'bg-indigo-500'}`}>
                        {user.status} ({user.is_verified})
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center gap-2">
                        <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">{user.fullName}</h1>
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${user.is_verified === 0 ? 'bg-amber-50 text-amber-600 border border-amber-200/60 dark:bg-amber-500/10 dark:text-amber-400' : 'bg-indigo-50 text-indigo-600 border border-indigo-200/60 dark:bg-indigo-500/10 dark:text-indigo-400'}`}>
                          is_verified: {user.is_verified} ({user.status})
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <Building2 size={14} className="text-slate-400" />
                        <span className="text-sm font-medium text-slate-600 dark:text-slate-300">{user.businessName}</span>
                      </div>
                      <button
                        onClick={() => userImageInputRef.current && userImageInputRef.current.click()}
                        className="mt-2.5 inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg text-xs font-semibold hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                      >
                        <Camera size={13} /> Change User Image (PNG)
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:flex sm:flex-col gap-3 w-full sm:w-auto text-sm text-slate-600 dark:text-slate-300">
                    <div className="flex items-center gap-2">
                      <Mail size={16} className="text-slate-400 shrink-0" /> <span className="truncate">{user.email}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone size={16} className="text-slate-400 shrink-0" /> {user.phone}
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin size={16} className="text-slate-400 shrink-0" /> {user.city}
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar size={16} className="text-slate-400 shrink-0" /> Joined {user.joinDate}
                    </div>
                  </div>
                </div>

                {/* Custom User Structured Frames Section */}
                <div className="bg-white dark:bg-slate-800 shadow-sm rounded-2xl border border-slate-200 dark:border-slate-700 p-6 flex flex-col gap-4">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 border-b border-slate-100 dark:border-slate-700/80 pb-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
                          <Layers size={18} />
                        </div>
                        <h3 className="font-bold text-slate-800 dark:text-slate-100 text-base">
                          User Frames ({userFrames.length})
                        </h3>
                        <span className="px-2 py-0.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-[11px] font-semibold rounded-md border border-blue-200 dark:border-blue-800">
                          Top + Bottom + Ratios
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        Frames with custom top/bottom PNGs, aspect ratios (1080:400 / 1080:400), and thumbnails
                      </p>
                    </div>

                    <button
                      onClick={handleOpenAddFramesModal}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-semibold transition-all shadow-sm shadow-blue-500/20 shrink-0"
                    >
                      <Plus size={15} /> Add Frames (Bulk)
                    </button>
                  </div>

                  {/* Render Structured Frames Grid */}
                  {loadingFrames ? (
                    <div className="py-8 flex items-center justify-center gap-2 text-slate-400 text-xs">
                      <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      Loading frames...
                    </div>
                  ) : userFrames.length === 0 ? (
                    <div className="py-8 px-4 text-center border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50/50 dark:bg-slate-900/30">
                      <Layers size={28} className="mx-auto text-slate-400 mb-2 opacity-50" />
                      <p className="text-xs font-semibold text-slate-600 dark:text-slate-300">No structured frames added yet</p>
                      <p className="text-[11px] text-slate-400 mt-0.5">Click "+ Add Frames (Bulk)" to add multiple frames with top & bottom aspect ratios.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {userFrames.map((f, idx) => (
                        <div
                          key={f.id || idx}
                          className="bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-200 dark:border-slate-700 p-3 flex flex-col justify-between relative group hover:border-blue-400 dark:hover:border-blue-500 transition-all shadow-sm"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                              Frame #{idx + 1}
                            </span>
                            <button
                              onClick={() => handleDeleteStructuredFrame(f.id)}
                              className="text-slate-400 hover:text-red-500 p-1 rounded-md hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
                              title="Delete frame"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>

                          {/* Visual Preview - Click to open Both Top & Bottom Modal */}
                          <div
                            onClick={() => setPreviewFrame(f)}
                            className="w-full h-32 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex flex-col justify-between overflow-hidden cursor-pointer relative p-1.5 group/preview"
                          >
                            {/* Top Strip Preview */}
                            {f.top_image_url ? (
                              <div className="w-full h-8 overflow-hidden rounded bg-slate-100 dark:bg-slate-700/60 flex items-center justify-center">
                                <img src={f.top_image_url} alt="Top Strip" className="w-full h-full object-contain" />
                              </div>
                            ) : (
                              <div className="w-full h-8 rounded bg-slate-100 dark:bg-slate-700/30 flex items-center justify-center text-[10px] text-slate-400 italic">
                                No Top PNG
                              </div>
                            )}

                            {/* Middle Placeholder / Thumbnail */}
                            <div className="flex-1 flex items-center justify-center py-1">
                              {f.thumbnail_url ? (
                                <img src={f.thumbnail_url} alt="Thumbnail" className="max-h-12 max-w-full object-contain opacity-80" />
                              ) : (
                                <span className="text-[10px] text-slate-400">Post Area</span>
                              )}
                            </div>

                            {/* Bottom Strip Preview */}
                            {f.bottom_image_url ? (
                              <div className="w-full h-10 overflow-hidden rounded bg-slate-100 dark:bg-slate-700/60 flex items-center justify-center">
                                <img src={f.bottom_image_url} alt="Bottom Strip" className="w-full h-full object-contain" />
                              </div>
                            ) : (
                              <div className="w-full h-10 rounded bg-slate-100 dark:bg-slate-700/30 flex items-center justify-center text-[10px] text-slate-400 italic">
                                No Bottom PNG
                              </div>
                            )}

                            {/* Hover overlay hint */}
                            <div className="absolute inset-0 bg-slate-900/50 opacity-0 group-hover/preview:opacity-100 transition-opacity flex items-center justify-center text-white text-[11px] font-semibold">
                              Click to Preview
                            </div>
                          </div>

                          {/* Aspect Ratios Badges */}
                          <div className="mt-2.5 flex flex-col gap-1 text-[11px]">
                            <div className="flex justify-between items-center text-slate-500 dark:text-slate-400">
                              <span>Top Ratio:</span>
                              <span className="font-mono font-semibold text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700">
                                {f.top_aspect_ratio || "1080:400"}
                              </span>
                            </div>
                            <div className="flex justify-between items-center text-slate-500 dark:text-slate-400">
                              <span>Bottom Ratio:</span>
                              <span className="font-mono font-semibold text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700">
                                {f.bottom_aspect_ratio || "1080:400"}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Usage Stats */}
                <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100 mt-2">App Usage & Activity</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-white dark:bg-slate-800 shadow-sm rounded-xl p-5 border border-slate-200 dark:border-slate-700 flex flex-col gap-2">
                    <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center mb-2">
                      <ImageIcon size={20} />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-800 dark:text-slate-100">{user.stats.templatesShared}</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Templates Shared</p>
                  </div>
                  <div className="bg-white dark:bg-slate-800 shadow-sm rounded-xl p-5 border border-slate-200 dark:border-slate-700 flex flex-col gap-2">
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-2">
                      <Crop size={20} />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-800 dark:text-slate-100">{user.stats.bgRemovals}</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Background Removals</p>
                  </div>
                  <div className="bg-white dark:bg-slate-800 shadow-sm rounded-xl p-5 border border-slate-200 dark:border-slate-700 flex flex-col gap-2">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mb-2">
                      <Activity size={20} />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-800 dark:text-slate-100">{user.stats.activeDays} Days</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">App Engagement</p>
                  </div>
                </div>

                {/* Recent Activity Table */}
                <div className="bg-white dark:bg-slate-800 shadow-sm rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden mt-2">
                  <div className="p-5 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
                    <h2 className="font-bold text-slate-800 dark:text-slate-100">Recent Template Downloads</h2>
                    <button className="text-sm text-blue-600 font-medium hover:underline">View All</button>
                  </div>
                  <div className="p-0">
                    <table className="w-full text-left">
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                        {[1, 2, 3].map((item) => (
                          <tr key={item} className="hover:bg-slate-50/50 dark:bg-slate-800/50 dark:bg-slate-900/50 transition-colors">
                            <td className="p-4">
                              <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-lg bg-slate-100 dark:bg-slate-700 overflow-hidden">
                                  <img src={`https://images.unsplash.com/photo-1582260667084-59e2ab176508?w=100&h=100&fit=crop&q=${item}`} alt="Template" className="w-full h-full object-cover" />
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">Festival Banner - Navratri</p>
                                  <p className="text-xs text-slate-500 dark:text-slate-400">Today at 10:45 AM</p>
                                </div>
                              </div>
                            </td>
                            <td className="p-4 text-right">
                              <span className="px-2.5 py-1 bg-green-50 text-green-600 text-xs font-medium rounded-lg">WhatsApp</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

              </div>

              {/* Right Panel: Subscription Status & Expiry */}
              <div className="w-full xl:w-80 flex flex-col gap-6 shrink-0">
                <div className="bg-white dark:bg-slate-800 shadow-sm rounded-xl border border-slate-200 dark:border-slate-700 p-6 flex flex-col gap-4">
                  <h3 className="font-bold text-slate-800 dark:text-slate-100 text-base">Subscription Plan</h3>

                  {subscriptionLoading ? (
                    <div className="py-8 flex flex-col items-center justify-center gap-2 text-slate-400">
                      <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-xs">Loading subscription...</span>
                    </div>
                  ) : hasActivePlan ? (
                    <>
                      <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/40 dark:to-orange-950/40 border border-amber-200/60 dark:border-amber-700/50 rounded-xl p-4 flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                          <span className="text-amber-700 dark:text-amber-400 font-bold text-lg flex items-center gap-1.5">
                            <Crown size={18} className="text-amber-500" />
                            {subData.plan_name}
                          </span>
                          <span className="bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-md uppercase">
                            {subData.status || "ACTIVE"}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-700 dark:text-slate-200 text-base font-bold">
                            {subData.price ? `₹${parseFloat(subData.price).toLocaleString('en-IN')}` : "Custom"}
                          </span>
                          {subData.duration_days && (
                            <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                              {subData.duration_days} Days Plan
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="space-y-3 mt-2">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-slate-500 dark:text-slate-400">Account Type</span>
                          <span className="text-amber-600 dark:text-amber-400 font-semibold capitalize">
                            {activeSubscription?.user_type || subData.user_type || "Premium"}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-slate-500 dark:text-slate-400">Database Status</span>
                          <span className="text-amber-600 dark:text-amber-400 font-bold font-mono">
                            is_verified = {subData.is_verified !== undefined ? subData.is_verified : 0}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-slate-500 dark:text-slate-400">Start Date</span>
                          <span className="text-slate-800 dark:text-slate-100 font-semibold">
                            {subData.start_date ? new Date(subData.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : "N/A"}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-slate-500 dark:text-slate-400">Expiry Date</span>
                          <span className="text-slate-800 dark:text-slate-100 font-semibold">
                            {subData.end_date ? new Date(subData.end_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : "N/A"}
                          </span>
                        </div>

                        {subData.razorpay_order_id && (
                          <div className="flex justify-between items-center text-xs">
                            <span className="text-slate-400">Order ID</span>
                            <span className="font-mono text-slate-600 dark:text-slate-300 truncate max-w-[140px]" title={subData.razorpay_order_id}>
                              {subData.razorpay_order_id}
                            </span>
                          </div>
                        )}
                        {subData.razorpay_payment_id && (
                          <div className="flex justify-between items-center text-xs">
                            <span className="text-slate-400">Payment ID</span>
                            <span className="font-mono text-slate-600 dark:text-slate-300 truncate max-w-[140px]" title={subData.razorpay_payment_id}>
                              {subData.razorpay_payment_id}
                            </span>
                          </div>
                        )}

                        <div className="h-px bg-slate-100 dark:bg-slate-700 my-2"></div>

                        <div className="flex flex-col gap-1.5">
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-slate-500 dark:text-slate-400 font-medium">Days Remaining</span>
                            <span className="text-amber-600 dark:text-amber-400 font-bold text-base">
                              {subData.days_left !== undefined ? subData.days_left : 0} Days
                            </span>
                          </div>
                          {/* Progress Bar */}
                          <div className="w-full bg-slate-100 dark:bg-slate-700 rounded-full h-2">
                            <div
                              className="bg-amber-500 h-2 rounded-full transition-all duration-500"
                              style={{
                                width: `${Math.min(100, Math.max(0, ((Number(subData.days_left) || 0) / (Number(subData.duration_days) || 30)) * 100))}%`
                              }}
                            ></div>
                          </div>
                        </div>

                        <div className="flex flex-col gap-2 pt-2">
                          <button
                            onClick={handleOpenUpgradeModal}
                            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold py-2 px-3 rounded-lg transition-colors flex items-center justify-center gap-1.5 shadow-sm"
                          >
                            <Crown size={14} /> Change / Extend Plan
                          </button>
                          <button
                            onClick={() => handleToggleVerifiedStatus("Free")}
                            className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-200 text-xs font-semibold py-2 px-3 rounded-lg transition-colors flex items-center justify-center gap-1.5"
                          >
                            Switch to Free (is_verified = 1)
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (activeSubscription?.is_premium || user.is_verified === 0) ? (
                    <div className="bg-amber-50 dark:bg-amber-950/40 border border-amber-200/60 dark:border-amber-700/50 rounded-xl p-4 flex flex-col gap-3">
                      <div className="flex items-center justify-between">
                        <span className="text-amber-700 dark:text-amber-400 font-bold text-base flex items-center gap-1.5">
                          <Crown size={16} /> Premium Access
                        </span>
                        <span className="bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-md">is_verified = 0</span>
                      </div>
                      <p className="text-xs text-slate-600 dark:text-slate-300">
                        User has active Premium status (is_verified = 0). No specific gateway subscription plan attached.
                      </p>
                      <button
                        onClick={handleOpenUpgradeModal}
                        className="w-full bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-1.5 shadow-sm"
                      >
                        <Crown size={14} /> Assign Subscription Plan
                      </button>
                      <button
                        onClick={() => handleToggleVerifiedStatus("Free")}
                        className="w-full bg-indigo-50 hover:bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 text-xs font-semibold py-2 px-4 rounded-lg transition-colors"
                      >
                        Switch to Free (is_verified = 1)
                      </button>
                    </div>
                  ) : (
                    <div className="bg-slate-50 dark:bg-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl p-5 text-center flex flex-col gap-3 items-center">
                      <div className="flex items-center gap-2">
                        <span className="text-slate-600 dark:text-slate-300 font-semibold text-sm">Free User</span>
                        <span className="bg-indigo-100 text-indigo-700 text-[10px] font-bold px-2 py-0.5 rounded-md">is_verified = 1</span>
                      </div>
                      <p className="text-xs text-slate-400">User is currently on the free tier. When subscription plan is taken, is_verified will become 0.</p>
                      <button
                        onClick={handleOpenUpgradeModal}
                        className="w-full bg-blue-600 text-white text-xs font-semibold py-2.5 px-4 rounded-lg hover:bg-blue-700 shadow-sm hover:shadow transition-all flex items-center justify-center gap-2"
                      >
                        <Crown size={15} /> Assign Plan (Set is_verified = 0)
                      </button>
                      <button
                        onClick={() => handleToggleVerifiedStatus("Premium")}
                        className="w-full bg-amber-50 hover:bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-200 dark:border-amber-800 text-xs font-semibold py-2 px-3 rounded-lg transition-colors"
                      >
                        Quick Set to Premium (is_verified = 0)
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Profile & Upload Images Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-2xl w-full max-w-2xl overflow-hidden animate-in zoom-in-95 duration-200 my-8">
            <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between bg-slate-50/70 dark:bg-slate-800/70">
              <h3 className="font-bold text-lg text-slate-800 dark:text-slate-100 flex items-center gap-2">
                <Edit2 size={18} className="text-blue-600" />
                Edit User Profile & Upload Images
              </h3>
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveModal}>
              <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">

                {/* Images Upload Section */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-100 dark:border-slate-700">
                  {/* User Profile Image */}
                  <div>
                    <label className="block text-xs font-bold text-slate-700 dark:text-slate-200 mb-2 uppercase tracking-wider">
                      User Profile Photo (PNG/JPG)
                    </label>
                    <div className="flex items-center gap-3">
                      <div className="w-16 h-16 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shrink-0">
                        <img
                          src={modalUserImagePreview || (user && user.userimage)}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg text-xs font-semibold hover:bg-blue-100 transition-colors">
                          <Upload size={13} /> Choose Image
                          <input
                            type="file"
                            accept="image/png, image/jpeg, image/jpg, image/webp"
                            className="hidden"
                            onChange={(e) => {
                              const f = e.target.files && e.target.files[0];
                              if (f) {
                                setModalUserImage(f);
                                setModalUserImagePreview(URL.createObjectURL(f));
                              }
                            }}
                          />
                        </label>
                        <p className="text-[11px] text-slate-400 mt-1 truncate">
                          {modalUserImage ? modalUserImage.name : "PNG, JPG, WEBP supported"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Business Logo */}
                  <div>
                    <label className="block text-xs font-bold text-slate-700 dark:text-slate-200 mb-2 uppercase tracking-wider">
                      Business Logo (Auto BG Removal)
                    </label>
                    <div className="flex items-center gap-3">
                      <div className="w-16 h-16 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-[radial-gradient(#cbd5e1_1px,transparent_1px)] [background-size:6px_6px] bg-white dark:bg-slate-800 shrink-0 flex items-center justify-center p-1">
                        <img
                          src={modalBusinessLogoPreview || (user && user.businessLogo)}
                          alt="Logo Preview"
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg text-xs font-semibold hover:bg-blue-100 transition-colors">
                          <Upload size={13} /> Choose Logo
                          <input
                            type="file"
                            accept="image/png, image/jpeg, image/jpg, image/webp"
                            className="hidden"
                            onChange={(e) => {
                              const f = e.target.files && e.target.files[0];
                              if (f) {
                                setModalBusinessLogo(f);
                                setModalBusinessLogoPreview(URL.createObjectURL(f));
                              }
                            }}
                          />
                        </label>
                        <p className="text-[11px] text-emerald-600 dark:text-emerald-400 font-medium mt-1">
                          ✓ Auto bg removal
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Bulk Frames Upload inside Modal */}
                  <div className="col-span-1 sm:col-span-2">
                    <label className="block text-xs font-bold text-slate-700 dark:text-slate-200 mb-2 uppercase tracking-wider">
                      User Frames (Bulk)
                    </label>
                    <div className="flex flex-col gap-3">
                      <div className="flex-1">
                        <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-lg text-xs font-semibold hover:bg-purple-100 transition-colors">
                          <Upload size={13} /> Choose Multiple Frames
                          <input
                            type="file"
                            accept="image/png, image/jpeg, image/jpg, image/webp"
                            className="hidden"
                            multiple
                            onChange={(e) => {
                              const files = e.target.files;
                              if (files) {
                                setModalFrames(prev => [...prev, ...Array.from(files)]);
                                const previews = Array.from(files).map(f => URL.createObjectURL(f));
                                setModalFramesPreview(prev => [...(prev || []), ...previews]);
                              }
                            }}
                          />
                        </label>
                        <p className="text-[11px] text-slate-400 mt-1 truncate">
                          {modalFrames && modalFrames.length > 0 ? `${modalFrames.length} files selected (click again to add more)` : "Select multiple frames (PNG/JPG)"}
                        </p>
                      </div>
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        {modalFramesPreview && modalFramesPreview.length > 0 ? (
                          modalFramesPreview.map((src, i) => (
                            <div key={i} onClick={() => setPreviewImage(src)} className="w-12 h-12 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 bg-[radial-gradient(#cbd5e1_1px,transparent_1px)] [background-size:8px_8px] bg-slate-50 dark:bg-slate-900 shrink-0 flex items-center justify-center p-1 cursor-pointer hover:border-blue-500 transition-colors">
                              <img
                                src={src}
                                alt="Preview"
                                className="max-w-full max-h-full object-contain"
                              />
                            </div>
                          ))
                        ) : (
                          user && user.frames && user.frames.slice(0, 5).map((f, i) => {
                            const imgSrc = typeof f === 'string' ? f : (f?.thumbnail_url || f?.top_image_url || f?.bottom_image_url);
                            if (!imgSrc) return null;
                            return (
                              <div key={i} onClick={() => setPreviewImage(imgSrc)} className="w-12 h-12 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 bg-[radial-gradient(#cbd5e1_1px,transparent_1px)] [background-size:8px_8px] bg-slate-50 dark:bg-slate-900 shrink-0 flex items-center justify-center p-1 cursor-pointer hover:border-blue-500 transition-colors">
                                <img
                                  src={imgSrc}
                                  alt="Preview"
                                  className="max-w-full max-h-full object-contain"
                                />
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Text Form Fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={modalFormData.fullname}
                      onChange={(e) => setModalFormData({ ...modalFormData, fullname: e.target.value })}
                      className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2 text-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={modalFormData.email}
                      onChange={(e) => setModalFormData({ ...modalFormData, email: e.target.value })}
                      className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2 text-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
                      Business Name
                    </label>
                    <input
                      type="text"
                      value={modalFormData.business_name}
                      onChange={(e) => setModalFormData({ ...modalFormData, business_name: e.target.value })}
                      className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2 text-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
                      City / Location
                    </label>
                    <input
                      type="text"
                      value={modalFormData.city}
                      onChange={(e) => setModalFormData({ ...modalFormData, city: e.target.value })}
                      className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2 text-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
                      Business Email
                    </label>
                    <input
                      type="email"
                      value={modalFormData.business_email}
                      onChange={(e) => setModalFormData({ ...modalFormData, business_email: e.target.value })}
                      className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2 text-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
                      Business Phone
                    </label>
                    <input
                      type="text"
                      value={modalFormData.business_phone}
                      onChange={(e) => setModalFormData({ ...modalFormData, business_phone: e.target.value })}
                      className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2 text-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
                      Business Address
                    </label>
                    <textarea
                      rows={2}
                      value={modalFormData.business_address}
                      onChange={(e) => setModalFormData({ ...modalFormData, business_address: e.target.value })}
                      className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2 text-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 resize-none"
                    />
                  </div>

                  <div className="sm:col-span-2 border-t border-slate-100 dark:border-slate-700 pt-3 mt-1">
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
                      Account Status & Plan (Free = 1, Premium = 0)
                    </label>
                    <select
                      value={modalFormData.status}
                      onChange={(e) => setModalFormData({ ...modalFormData, status: e.target.value })}
                      className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2 text-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 cursor-pointer"
                    >
                      <option value="Free">Free (is_verified = 1)</option>
                      <option value="Premium">Premium (is_verified = 0)</option>
                    </select>
                  </div>
                </div>

              </div>

              <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-700 flex items-center justify-end gap-3 bg-slate-50/70 dark:bg-slate-800/70">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-4 py-2 text-sm font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingModal}
                  className="flex items-center gap-2 px-6 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-sm hover:shadow-md transition-all disabled:opacity-50"
                >
                  <Save size={16} />
                  {savingModal ? "Saving Changes..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upgrade / Assign Plan Modal */}
      {isUpgradeModalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between bg-slate-50/70 dark:bg-slate-800/70">
              <h3 className="font-bold text-base text-slate-800 dark:text-slate-100 flex items-center gap-2">
                <Crown size={18} className="text-amber-500" />
                Assign Subscription Plan
              </h3>
              <button
                onClick={() => setIsUpgradeModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1 rounded-lg"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Assigning a subscription plan will activate the user's plan and set <code className="bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 px-1 py-0.5 rounded font-mono font-bold">is_verified = 0</code> (Premium).
              </p>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1.5">
                  Select Subscription Plan
                </label>
                <select
                  value={selectedPlanId}
                  onChange={(e) => {
                    setSelectedPlanId(e.target.value);
                    const chosen = availablePlans.find(p => String(p.plan_id_PK) === e.target.value);
                    if (chosen && chosen.duration_days) {
                      setCustomDuration(String(chosen.duration_days));
                    }
                  }}
                  className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2.5 text-sm bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 outline-none focus:border-blue-500"
                >
                  {availablePlans.length === 0 && <option value="">No predefined plans found</option>}
                  {availablePlans.map((p) => (
                    <option key={p.plan_id_PK} value={p.plan_id_PK}>
                      {p.plan_name} — ₹{p.price} ({p.duration_days} Days)
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1.5">
                  Plan Duration (in days)
                </label>
                <input
                  type="number"
                  min="1"
                  value={customDuration}
                  onChange={(e) => setCustomDuration(e.target.value)}
                  className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2 text-sm bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 outline-none focus:border-blue-500"
                  placeholder="30"
                />
              </div>

              <div className="bg-amber-50/70 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 rounded-xl p-3 text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2">
                <CheckCircle size={16} className="shrink-0 text-amber-600 mt-0.5" />
                <span>Result: User will immediately be converted to Premium with <strong>is_verified: 0</strong>.</span>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-700 flex items-center justify-end gap-3 bg-slate-50/70 dark:bg-slate-800/70">
              <button
                type="button"
                onClick={() => setIsUpgradeModalOpen(false)}
                className="px-4 py-2 text-sm font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAssignPlan}
                disabled={assigningPlan}
                className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-sm disabled:opacity-50"
              >
                <Crown size={15} />
                {assigningPlan ? "Assigning..." : "Confirm & Set is_verified = 0"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Custom Bulk Frames Modal */}
      {isAddFramesModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl max-w-4xl w-full my-8 overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50/70 dark:bg-slate-800/50">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                  <Layers size={18} />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800 dark:text-slate-100 text-base">Add User Frames (Bulk)</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Configure Top Image, Bottom Image, Aspect Ratios & Thumbnail</p>
                </div>
              </div>
              <button
                onClick={() => !uploadingStructuredFrames && setIsAddFramesModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Body - Dynamic Frame Rows */}
            <div className="p-6 overflow-y-auto flex-1 space-y-5">
              {frameRows.map((row, idx) => (
                <div
                  key={idx}
                  className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 relative"
                >
                  <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-200/60 dark:border-slate-700/60">
                    <span className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider">
                      Frame #{idx + 1}
                    </span>
                    {frameRows.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveFrameRow(idx)}
                        className="text-red-500 hover:text-red-700 text-xs font-semibold flex items-center gap-1 hover:bg-red-50 dark:hover:bg-red-950/40 px-2 py-1 rounded-lg transition-colors"
                      >
                        <Trash2 size={13} /> Remove
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Top Image + Top Aspect Ratio */}
                    <div className="bg-white dark:bg-slate-800/80 p-3 rounded-xl border border-slate-200/80 dark:border-slate-700">
                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1.5">
                        1. Top Image (PNG)
                      </label>
                      <div className="flex items-center gap-3">
                        <label className="flex-1 cursor-pointer flex flex-col items-center justify-center p-2.5 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg hover:border-blue-500 transition-colors bg-slate-50/50 dark:bg-slate-900/50">
                          <Upload size={16} className="text-slate-400 mb-1" />
                          <span className="text-[11px] font-medium text-slate-600 dark:text-slate-300 truncate max-w-[140px]">
                            {row.top_image ? row.top_image.name : "Select Top PNG"}
                          </span>
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            className="hidden"
                            onChange={(e) => handleFrameRowFileChange(idx, "top_image", e.target.files && e.target.files[0])}
                          />
                        </label>
                        {row.top_image_preview && (
                          <div
                            onClick={() => setPreviewImage(row.top_image_preview)}
                            className="w-14 h-14 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-900 shrink-0 flex items-center justify-center p-1 cursor-pointer"
                          >
                            <img src={row.top_image_preview} alt="Top Preview" className="max-w-full max-h-full object-contain" />
                          </div>
                        )}
                      </div>
                      <div className="mt-2.5">
                        <label className="block text-[11px] font-medium text-slate-500 dark:text-slate-400 mb-1">
                          Top Aspect Ratio
                        </label>
                        <input
                          type="text"
                          value={row.top_aspect_ratio}
                          onChange={(e) => handleFrameRowChange(idx, "top_aspect_ratio", e.target.value)}
                          placeholder="1080:400"
                          className="w-full text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 outline-none focus:border-blue-500"
                        />
                      </div>
                    </div>

                    {/* Bottom Image + Bottom Aspect Ratio */}
                    <div className="bg-white dark:bg-slate-800/80 p-3 rounded-xl border border-slate-200/80 dark:border-slate-700">
                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1.5">
                        2. Bottom Image (PNG)
                      </label>
                      <div className="flex items-center gap-3">
                        <label className="flex-1 cursor-pointer flex flex-col items-center justify-center p-2.5 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg hover:border-blue-500 transition-colors bg-slate-50/50 dark:bg-slate-900/50">
                          <Upload size={16} className="text-slate-400 mb-1" />
                          <span className="text-[11px] font-medium text-slate-600 dark:text-slate-300 truncate max-w-[140px]">
                            {row.bottom_image ? row.bottom_image.name : "Select Bottom PNG"}
                          </span>
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            className="hidden"
                            onChange={(e) => handleFrameRowFileChange(idx, "bottom_image", e.target.files && e.target.files[0])}
                          />
                        </label>
                        {row.bottom_image_preview && (
                          <div
                            onClick={() => setPreviewImage(row.bottom_image_preview)}
                            className="w-14 h-14 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-900 shrink-0 flex items-center justify-center p-1 cursor-pointer"
                          >
                            <img src={row.bottom_image_preview} alt="Bottom Preview" className="max-w-full max-h-full object-contain" />
                          </div>
                        )}
                      </div>
                      <div className="mt-2.5">
                        <label className="block text-[11px] font-medium text-slate-500 dark:text-slate-400 mb-1">
                          Bottom Aspect Ratio
                        </label>
                        <input
                          type="text"
                          value={row.bottom_aspect_ratio}
                          onChange={(e) => handleFrameRowChange(idx, "bottom_aspect_ratio", e.target.value)}
                          placeholder="1080:400"
                          className="w-full text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 outline-none focus:border-blue-500"
                        />
                      </div>
                    </div>

                    {/* Thumbnail Image */}
                    <div className="bg-white dark:bg-slate-800/80 p-3 rounded-xl border border-slate-200/80 dark:border-slate-700 flex flex-col justify-between">
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1.5">
                          3. Thumbnail Image
                        </label>
                        <div className="flex items-center gap-3">
                          <label className="flex-1 cursor-pointer flex flex-col items-center justify-center p-2.5 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg hover:border-blue-500 transition-colors bg-slate-50/50 dark:bg-slate-900/50">
                            <Upload size={16} className="text-slate-400 mb-1" />
                            <span className="text-[11px] font-medium text-slate-600 dark:text-slate-300 truncate max-w-[140px]">
                              {row.thumbnail_image ? row.thumbnail_image.name : "Select Thumb"}
                            </span>
                            <input
                              type="file"
                              accept="image/png,image/jpeg,image/webp"
                              className="hidden"
                              onChange={(e) => handleFrameRowFileChange(idx, "thumbnail_image", e.target.files && e.target.files[0])}
                            />
                          </label>
                          {row.thumbnail_image_preview && (
                            <div
                              onClick={() => setPreviewImage(row.thumbnail_image_preview)}
                              className="w-14 h-14 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-900 shrink-0 flex items-center justify-center p-1 cursor-pointer"
                            >
                              <img src={row.thumbnail_image_preview} alt="Thumb Preview" className="max-w-full max-h-full object-contain" />
                            </div>
                          )}
                        </div>
                      </div>
                      <p className="text-[10px] text-slate-400 mt-2">
                        Preview card thumbnail in app / admin panel.
                      </p>
                    </div>
                  </div>
                </div>
              ))}

              {/* Add Another Frame Button */}
              <button
                type="button"
                onClick={handleAddFrameRow}
                className="w-full py-2.5 border-2 border-dashed border-blue-300 dark:border-blue-800 hover:border-blue-500 rounded-xl text-blue-600 dark:text-blue-400 text-xs font-semibold flex items-center justify-center gap-2 hover:bg-blue-50/50 dark:hover:bg-blue-950/20 transition-all"
              >
                <Plus size={15} /> Add Another Frame Set
              </button>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50/70 dark:bg-slate-800/50">
              <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                {frameRows.length} Frame set(s) configured
              </span>
              <div className="flex gap-2.5">
                <button
                  type="button"
                  onClick={() => setIsAddFramesModalOpen(false)}
                  disabled={uploadingStructuredFrames}
                  className="px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveStructuredFrames}
                  disabled={uploadingStructuredFrames}
                  className="flex items-center gap-2 px-5 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-sm disabled:opacity-50 transition-all"
                >
                  <Upload size={14} />
                  {uploadingStructuredFrames ? "Uploading Frames..." : `Upload All (${frameRows.length}) Frames`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Both Top & Bottom Frame Preview Modal */}
      {previewFrame && (
        <div
          className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center z-[100] p-4 overflow-y-auto"
          onClick={() => setPreviewFrame(null)}
        >
          <div
            className="relative bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl max-w-4xl w-full my-6 overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50/70 dark:bg-slate-800/50">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                  <Layers size={18} />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800 dark:text-slate-100 text-base">
                    Frame Preview (Top & Bottom)
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Detailed view of Top image, Bottom image, and Poster Mockup
                  </p>
                </div>
              </div>
              <button
                onClick={() => setPreviewFrame(null)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto space-y-6 flex-1">
              {/* Section 1: Combined Poster Mockup (Top + Bottom assembled together) */}
              <div className="bg-slate-50/60 dark:bg-slate-800/30 p-4 rounded-xl border border-slate-200/80 dark:border-slate-800">
                <div className="flex items-center justify-between mb-2.5">
                  <span className="text-xs font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wide flex items-center gap-1.5">
                    <Layers size={14} className="text-blue-500" />
                    Combined Poster Preview (Mockup)
                  </span>
                  <span className="text-[11px] text-slate-400">
                    How frame appears on post
                  </span>
                </div>
                <div className="mx-auto max-w-sm aspect-square bg-[radial-gradient(#cbd5e1_1px,transparent_1px)] [background-size:12px_12px] bg-slate-100 dark:bg-slate-950 rounded-xl border-2 border-slate-300 dark:border-slate-700 overflow-hidden flex flex-col justify-between shadow-inner relative">
                  {/* Top Strip */}
                  {previewFrame.top_image_url ? (
                    <div className="w-full bg-white/40 dark:bg-black/20 flex items-center justify-center border-b border-dashed border-blue-400/50">
                      <img
                        src={previewFrame.top_image_url}
                        alt="Top Strip"
                        className="w-full object-contain max-h-16"
                      />
                    </div>
                  ) : (
                    <div className="w-full py-2 bg-slate-200/40 dark:bg-slate-800/40 text-[10px] text-slate-400 text-center italic">
                      No Top Image
                    </div>
                  )}

                  {/* Center Post Content Placeholder */}
                  <div className="flex-1 flex flex-col items-center justify-center p-4 text-center">
                    {previewFrame.thumbnail_url ? (
                      <img
                        src={previewFrame.thumbnail_url}
                        alt="Thumbnail"
                        className="max-h-24 max-w-full object-contain opacity-70"
                      />
                    ) : (
                      <span className="text-xs font-medium text-slate-400 dark:text-slate-500">
                        [ Banner / Poster Content Area ]
                      </span>
                    )}
                  </div>

                  {/* Bottom Strip */}
                  {previewFrame.bottom_image_url ? (
                    <div className="w-full bg-white/40 dark:bg-black/20 flex items-center justify-center border-t border-dashed border-blue-400/50">
                      <img
                        src={previewFrame.bottom_image_url}
                        alt="Bottom Strip"
                        className="w-full object-contain max-h-24"
                      />
                    </div>
                  ) : (
                    <div className="w-full py-2 bg-slate-200/40 dark:bg-slate-800/40 text-[10px] text-slate-400 text-center italic">
                      No Bottom Image
                    </div>
                  )}
                </div>
              </div>

              {/* Section 2: Individual Images Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Top Image Details */}
                <div className="bg-slate-50 dark:bg-slate-800/40 p-4 rounded-xl border border-slate-200 dark:border-slate-800 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs font-bold text-slate-800 dark:text-slate-100">
                        1. Top Image
                      </h4>
                      <span className="font-mono text-xs font-semibold px-2 py-0.5 rounded bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800">
                        Ratio: {previewFrame.top_aspect_ratio || "1080:400"}
                      </span>
                    </div>

                    <div
                      onClick={() => previewFrame.top_image_url && setPreviewImage(previewFrame.top_image_url)}
                      className="w-full h-36 rounded-lg bg-[radial-gradient(#cbd5e1_1px,transparent_1px)] [background-size:10px_10px] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 flex items-center justify-center p-2 overflow-hidden cursor-pointer hover:border-blue-500 transition-colors group/box relative"
                      title="Click to view full image"
                    >
                      {previewFrame.top_image_url ? (
                        <>
                          <img
                            src={previewFrame.top_image_url}
                            alt="Top"
                            className="max-w-full max-h-full object-contain"
                          />
                          <div className="absolute inset-0 bg-slate-900/50 opacity-0 group-hover/box:opacity-100 transition-opacity flex items-center justify-center text-white text-xs font-semibold">
                            Click for Full Image
                          </div>
                        </>
                      ) : (
                        <span className="text-xs text-slate-400 italic">No Top Image Available</span>
                      )}
                    </div>
                  </div>

                  {previewFrame.top_image_url && (
                    <a
                      href={previewFrame.top_image_url}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-3 text-center text-xs text-blue-600 dark:text-blue-400 hover:underline font-medium"
                    >
                      Open Top Image in New Tab ↗
                    </a>
                  )}
                </div>

                {/* Bottom Image Details */}
                <div className="bg-slate-50 dark:bg-slate-800/40 p-4 rounded-xl border border-slate-200 dark:border-slate-800 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs font-bold text-slate-800 dark:text-slate-100">
                        2. Bottom Image
                      </h4>
                      <span className="font-mono text-xs font-semibold px-2 py-0.5 rounded bg-emerald-50 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                        Ratio: {previewFrame.bottom_aspect_ratio || "1080:400"}
                      </span>
                    </div>

                    <div
                      onClick={() => previewFrame.bottom_image_url && setPreviewImage(previewFrame.bottom_image_url)}
                      className="w-full h-36 rounded-lg bg-[radial-gradient(#cbd5e1_1px,transparent_1px)] [background-size:10px_10px] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 flex items-center justify-center p-2 overflow-hidden cursor-pointer hover:border-blue-500 transition-colors group/box relative"
                      title="Click to view full image"
                    >
                      {previewFrame.bottom_image_url ? (
                        <>
                          <img
                            src={previewFrame.bottom_image_url}
                            alt="Bottom"
                            className="max-w-full max-h-full object-contain"
                          />
                          <div className="absolute inset-0 bg-slate-900/50 opacity-0 group-hover/box:opacity-100 transition-opacity flex items-center justify-center text-white text-xs font-semibold">
                            Click for Full Image
                          </div>
                        </>
                      ) : (
                        <span className="text-xs text-slate-400 italic">No Bottom Image Available</span>
                      )}
                    </div>
                  </div>

                  {previewFrame.bottom_image_url && (
                    <a
                      href={previewFrame.bottom_image_url}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-3 text-center text-xs text-blue-600 dark:text-blue-400 hover:underline font-medium"
                    >
                      Open Bottom Image in New Tab ↗
                    </a>
                  )}
                </div>
              </div>

              {/* Thumbnail (if present) */}
              {previewFrame.thumbnail_url && (
                <div className="bg-slate-50 dark:bg-slate-800/40 p-4 rounded-xl border border-slate-200 dark:border-slate-800 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      onClick={() => setPreviewImage(previewFrame.thumbnail_url)}
                      className="w-16 h-16 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 flex items-center justify-center p-1 overflow-hidden cursor-pointer hover:border-blue-500"
                    >
                      <img
                        src={previewFrame.thumbnail_url}
                        alt="Thumbnail"
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-slate-800 dark:text-slate-100">
                        Thumbnail Image
                      </h4>
                      <p className="text-[11px] text-slate-400">
                        Displayed as the icon preview
                      </p>
                    </div>
                  </div>
                  <a
                    href={previewFrame.thumbnail_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline font-medium"
                  >
                    View Full ↗
                  </a>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-end bg-slate-50/70 dark:bg-slate-800/50">
              <button
                type="button"
                onClick={() => setPreviewFrame(null)}
                className="px-5 py-2 text-xs font-semibold text-white bg-slate-700 hover:bg-slate-800 rounded-xl transition-all"
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center z-[100] p-4" onClick={() => setPreviewImage(null)}>
          <div className="relative max-w-4xl max-h-[90vh] w-full flex items-center justify-center animate-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute -top-10 -right-2 text-white hover:text-red-400 transition-colors p-2 bg-slate-800/50 rounded-full"
            >
              <X size={24} />
            </button>
            <img src={previewImage} alt="Large Preview" className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl" />
          </div>
        </div>
      )}

      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
            display: none;
        }
        .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
        }
      `}</style>
    </div>
  );
}
